/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import gsap from "gsap";
import "gsap/EasePack";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import ScrollSmoother from "gsap/ScrollSmoother";
gsap.registerPlugin(ScrollSmoother, ScrollTrigger, SplitText);

import Swiper from "swiper";
import {
	Autoplay,
	FreeMode,
	Navigation,
	Pagination,
	Parallax,
	Scrollbar,
} from "swiper/modules";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// Default : Smooth scrolling
let scrollSmooth = false;

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
	// remove 'page-no-js' body class
	document.body.classList.remove("page-no-js");
	gsapPreloader();
	scrollDetection(160);
	menuBurger();
	menuDropdown();
	bannerInit();
	bannerToMarquee();
	dataSrc();
	dataClone();
	dataPopin();
	dataToggle();
});

// DOCUMENT RESIZED (avec ScrollTrigger)
ScrollTrigger.addEventListener("refreshInit", () => {
	gsapScrollSmootherRefresh();
	bannerToMarquee();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Activation/désactivation du scroll
function scrollEnabled(bool) {
	if (bool === false || bool === true) {
		const enableScroll = bool === true;

		if (scrollSmooth) {
			scrollSmooth.paused(!enableScroll);
		}

		document.documentElement.style.overflowY = enableScroll ? "auto" : "hidden";
	}
}

// Scroll vers une ancre
const scrollToAnchor = (scrollOffset = 100) => {
	// Fonction pour gérer le défilement
	const scrollToElement = (target) => {
		const element = document.querySelector(target);
		if (element) {
			if (scrollSmooth) {
				// Avec ScrollSmoother
				scrollSmooth.scrollTo(target, true, `top ${scrollOffset}px`);
			} else {
				// Par défaut
				window.scrollTo({
					top:
						element.getBoundingClientRect().top + window.scrollY - scrollOffset,
					behavior: "smooth",
				});
			}
		}
	};

	// au chargement
	const hashInUrl = window.location.hash;
	if (hashInUrl) {
		scrollToElement(hashInUrl);
	}

	// avec un [href="#..."]
	const linksWithAnchor = document.querySelectorAll('a[href^="#"]');
	linksWithAnchor.forEach((link) => {
		link.addEventListener("click", (event) => {
			const anchorHref = link.getAttribute("href");
			if (anchorHref !== "#") {
				event.preventDefault();
				scrollToElement(anchorHref);
			}
		});
	});

	// avec un [data-scrollto]
	const dataWithScrollTo = document.querySelectorAll("[data-scrollto]");
	dataWithScrollTo.forEach((data) => {
		data.addEventListener("click", () => {
			let anchorData = data.getAttribute("data-scrollto");
			if (anchorData === "auto") {
				// Calculer la position de défilement pour 'auto'
				const autoOffset = window.innerHeight + window.scrollY - scrollOffset;
				window.scrollTo({
					top: autoOffset,
					behavior: "smooth",
				});
			} else {
				// Utiliser la valeur de data-scrollto comme cible pour scrollToElement
				scrollToElement(anchorData);
			}
		});
	});
};

// Détection du scroll sur le body
const scrollDetection = (scrollOffset = 1) => {
	let position = window.scrollY;

	if (position > scrollOffset) {
		document.body.classList.add("page-scrolled");
	} else {
		document.body.classList.remove("page-scrolled");
	}

	document.addEventListener("scroll", () => {
		// Détecter la direction du scroll
		let scroll = window.scrollY;
		// Scroll vers le bas
		if (scroll > position) {
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
				document.body.classList.remove("page-scrolling");
			} else {
				document.body.classList.add("page-scrolling");
				document.body.classList.add("page-scrolled");
			}
		}
		// Scroll vers le haut
		else {
			document.body.classList.remove("page-scrolling");
			if (scroll < scrollOffset) {
				document.body.classList.remove("page-scrolled");
			}
		}
		// Scroll max ?
		if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
			document.body.classList.add("page-scroll-completed");
		} else {
			document.body.classList.remove("page-scroll-completed");
		}
		position = scroll;
	});
};

// Gestion du menu burger
const menuBurger = () => {
	const headerBurger = document.querySelector("#page-header-burger");
	const headerBackdrop = document.querySelector("#page-header-backdrop");

	if (headerBurger) {
		headerBurger.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
				gsapMenuBurger();
			}
		});
	}

	if (headerBackdrop) {
		headerBackdrop.addEventListener("click", () => {
			if (document.body.classList.contains("page-with-fullmenu")) {
				scrollEnabled(true);
				document.body.classList.remove("page-with-fullmenu");
			} else {
				scrollEnabled(false);
				document.body.classList.add("page-with-fullmenu");
			}
		});
	}
};

// Gestion des dropdowns
const menuDropdown = () => {
	const menuItemLinks = document.querySelectorAll(
		'.menu .menu__item .menu__link[href="#"]',
	);

	menuItemLinks.forEach((item) => {
		item.closest(".menu__item").classList.add("menu__item--parent");
		item.addEventListener("click", (event) => {
			event.preventDefault();
			item.closest(".menu__item").classList.toggle("menu__item--active");
		});
	});

	document.addEventListener("mouseup", (event) => {
		const activeMenuItems = document.querySelectorAll(
			".menu .menu__item.menu__item--active",
		);

		activeMenuItems.forEach((activeItem) => {
			if (
				!activeItem.isEqualNode(event.target) &&
				!activeItem.contains(event.target)
			) {
				activeItem.classList.remove("menu__item--active");
			}
		});
	});
};

// Activation/désactivation de la bannière
const bannerInit = () => {
	// Vérifie si la bannière a déjà été fermée durant cette session
	if (sessionStorage.getItem("bannerClosed") === "true") {
		return;
	}

	const pageBanner = document.querySelector("#page-banner");
	const pageBannerClose = document.querySelector("#page-banner-close");

	if (pageBanner) {
		// actualisation
		document.body.classList.add("page-with-banner");
		// bouton de fermeture
		pageBannerClose.addEventListener("click", () => {
			document.body.classList.remove("page-with-banner");

			// Enregistre en session que la bannière a été fermée
			sessionStorage.setItem("bannerClosed", "true");
		});
	}
};

// Gestion du défilement horizontal de la bannière
const bannerToMarquee = () => {
	const pageBanner = document.querySelector("#page-banner");

	if (pageBanner) {
		const bannerwrapperWidth = pageBanner.offsetWidth;
		const bannerTextWidth =
			pageBanner.querySelector(".banner__text").offsetWidth;
		if (bannerwrapperWidth > bannerTextWidth + 60) {
			pageBanner.classList.remove("page__banner--scrolling");
		} else {
			pageBanner.classList.add("page__banner--scrolling");
		}
	}
};

// Convertir les [data-src] en [src]
const dataSrc = () => {
	// Select all elements with a data-src attribute
	const srcElements = document.querySelectorAll("[data-src]");
	srcElements.forEach((element) => {
		element.setAttribute("src", element.getAttribute("data-src"));
		element.removeAttribute("data-src");
	});
};

// Cloner les éléménts [data-clone]
const dataClone = () => {
	const cloneTargets = document.querySelectorAll("[data-clone]");

	cloneTargets.forEach((cloneOriginal) => {
		const cloneValue = cloneOriginal.getAttribute("data-clone");
		const cloneParent = cloneOriginal.parentNode;

		if (!cloneParent) return;
		let cloneCount;

		if (cloneValue === "auto" || !cloneValue || cloneValue === undefined) {
			const cloneWidth = cloneOriginal.offsetWidth;
			cloneCount = Math.ceil(window.innerWidth / cloneWidth) + 1;
			// console.log("cloneCount: " + cloneCount);
		} else {
			const parsedCount = parseInt(cloneValue, 10);
			if (isNaN(parsedCount) || parsedCount <= 0) return;
			cloneCount = parsedCount;
		}

		for (let i = 0; i < cloneCount; i++) {
			const clone = cloneOriginal.cloneNode(true);
			clone.removeAttribute("data-clone");
			clone.setAttribute("data-clone-child", `${i + 1}/${cloneCount}`);
			cloneParent.insertBefore(clone, cloneOriginal);
		}

		cloneOriginal.remove(); // Supprimer l'élément initial après clonage
	});
};

// Gestions des actions des [data-popin]
const dataPopin = () => {
	const popins = document.querySelectorAll("[data-popin]");

	popins.forEach((popin) => {
		popin.addEventListener("click", (event) => {
			// event.preventDefault();

			// ouvrir/fermer la popin
			const thispopinName = popin.dataset.popin;
			if (thispopinName) {
				const thispopinEl = document.querySelector(
					`.page__popin#${thispopinName}`,
				);
				thispopinEl.classList.toggle("page__popin--open");
			}

			// mettre les vidéos des popins en pause
			const htmlVideoFrompopin =
				document.querySelectorAll(".page__popin video");
			if (htmlVideoFrompopin.length > 0) {
				htmlVideoFrompopin.forEach((video) => {
					video.pause();
				});
			}
		});
	});
};

// Gestions des actions des [data-toggle]
const dataToggle = () => {
	// Récupération de tous les écouteurs
	const toggleListeners = document.querySelectorAll("[data-toggle]");

	// Écouteur d'événement pour chaque écouteur
	toggleListeners.forEach((togglelistener) => {
		const toggleTargetSelector = togglelistener.dataset.toggle;
		const toggleTarget = toggleTargetSelector
			? document.querySelector(toggleTargetSelector)
			: null;

		togglelistener.addEventListener("click", (event) => {
			if (toggleTarget) {
				// event.stopPropagation();
				toggleTarget.classList.toggle("--active");
			}
		});
	});

	// Gestionnaire pour les clics hors des éléments toggle
	document.addEventListener("click", (event) => {
		toggleListeners.forEach((togglelistener) => {
			const toggleTargetSelector = togglelistener.dataset.toggle;
			const toggleTarget = toggleTargetSelector
				? document.querySelector(toggleTargetSelector)
				: null;
			if (
				toggleTarget &&
				toggleTarget.classList.contains("--active") &&
				!toggleTarget.contains(event.target) &&
				!togglelistener.contains(event.target)
			) {
				toggleTarget.classList.remove("--active");
			}
		});
	});
};

/* -------------------------------- (SWIPER) -------------------------------- */

// Swiper : Initialisation des sliders
const swiperInit = () => {
	const swiperElements = document.querySelectorAll(".swiper[data-swiper]");
	swiperElements.forEach((swiper) => {
		const slides = swiper.querySelectorAll(".swiper-wrapper .swiper-slide");
		if (slides.length > 0) {
			const swiperOptions = swiper.dataset.swiper.trim().split(/\s+/);

			// Vérifier si le viewport est assez large pour 'no-mobile'
			const isNoMobile = swiperOptions.includes("no-mobile");
			const isViewportLarge = window.innerWidth > 768;

			if (isNoMobile && !isViewportLarge) return;

			const config = {
				slidesPerView: "auto",
				speed: 600,
				modules: [
					Autoplay,
					FreeMode,
					Navigation,
					Pagination,
					Parallax,
					Scrollbar,
				],
				navigation: {
					prevEl: swiper.querySelector(".swiper-navigation-prev"),
					nextEl: swiper.querySelector(".swiper-navigation-next"),
					disabledClass: "--disabled",
					lockClass: "--hidden",
				},
				pagination: {
					el: swiper.querySelector(".swiper-pagination"),
					clickable: true,
					hiddenClass: "--hidden",
				},
				scrollbar: {
					el: swiper.querySelector(".swiper-scrollbar"),
					draggable: true,
					hide: true,
				},
				parallax: true,
			};

			if (swiperOptions.includes("autoplay"))
				config.autoplay = { delay: 4500, disableOnInteraction: false };
			if (swiperOptions.includes("loop")) config.loop = true;
			if (swiperOptions.includes("free")) config.freeMode = true;
			if (swiperOptions.includes("centered")) config.centeredSlides = true;

			const swiperInstance = new Swiper(swiper, config);
		}
	});

	// testimonials
	const testimonialContainer = document.querySelector('.archive-testimonial');
	if (testimonialContainer) {
		const testimonialSwiper = new Swiper(
			testimonialContainer.querySelector('.swiper'), {
			slidesPerView:1,
			spaceBetween:20,
			breakpoints:{
				768:{
					slidesPerView:2,
				},
				1200:{
					slidesPerView:3,
				},
			},
			modules: [
				Navigation,
				Pagination,
			],
			navigation: {
				prevEl: testimonialContainer.querySelector(".swiper-navigation-prev"),
				nextEl: testimonialContainer.querySelector(".swiper-navigation-next"),
				disabledClass: "--disabled",
				lockClass: "--hidden",
			},
			pagination: {
				el: testimonialContainer.querySelector(".swiper-pagination"),
				clickable: true,
				hiddenClass: "--hidden",
			},
		})
	}
};

/* --------------------------------- (GSAP) --------------------------------- */

// GSAP : Initialisation du scrollSmoother (avec ScrollSmoother)
const initScrollSmooth = () => {
	scrollSmooth = ScrollSmoother.create({
		wrapper: "#page-wrapper",
		content: "#page-container",
		smooth: 1,
		smoothTouch: false,
		effects: true,
		normalizeScroll: false,
		ignoreMobileResize: true,
	});
};

// GSAP : Rafraîchissement du ScrollSmoother (avec ScrollSmoother)
const gsapScrollSmootherRefresh = () => {
	if (scrollSmooth) {
		// Désactiver sur Élémentor
		if (document.querySelector("body.elementor-editor-active")) {
			scrollSmooth.kill();
		} else {
			scrollSmooth.refresh();
		}
	}
};

// GSAP : Animations liés au menuBurger
const gsapMenuBurger = () => {
	const burgerTimeline = gsap.timeline();
	burgerTimeline.from(["#page-header .header__fullmenu .menu__item"], {
		duration: 0.6,
		stagger: 0.05,
		ease: "power1.out",
		autoAlpha: 0,
		x: 40,
	});
};

// GSAP : Animations au scroll (avec ScrollTrigger)
const gsapDataAnimation = () => {
	// [data-animation] + .elementor-element
	const elementsToAnimated = document.querySelectorAll([
		"#page-container [data-animation]",
		"#page-container .elementor-heading-title",
		"#page-container .elementor-element:not([data-animation]) > .elementor-element",
	]);
	elementsToAnimated.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0",
			onEnter: () => {
				setTimeout(() => {
					_this.classList.add("--animated");
				}, delay);
			},
			onLeaveBack: () => {
				_this.classList.remove("--animated");
			},
			// markers: true,
		});
	});

	// data-animation="children-to-top"
	const childrenToTop = document.querySelectorAll(
		"[data-animation='children-to-top']",
	);
	childrenToTop.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		gsap.from(_this.children, {
			y: "50%",
			autoAlpha: 0,
			duration: 1.2,
			ease: "power4.out",
			delay: delay,
			stagger: 0.1,
		});
	});

	// data-animation="reveal-text"
	const textsToAnimated = document.querySelectorAll([
		// global
		'#page-container [data-animation="reveal-text"]',
		"#page-container .elementor-element:not([data-animation]) > .elementor-widget-container > .elementor-heading-title",
	]);
	textsToAnimated.forEach((_thisText) => {
		const delay = _thisText.getAttribute("data-animation-delay")
			? parseInt(_thisText.getAttribute("data-animation-delay"), 10) / 1000
			: 0;
		const split = SplitText.create(_thisText, {
			type: "lines, words",
			autoSplit: true,
			mask: "lines",
			onSplit(self) {
				gsap.from(self.words, {
					scrollTrigger: {
						trigger: _thisText,
						start: "top 88%",
						toggleActions: "play none none reverse",
						// markers: true,
					},
					y: "100%",
					autoAlpha: 0,
					delay: delay,
					duration: 0.8,
					ease: "power3.out",
					stagger: 0.05,
				});
			},
		});
	});

	// data-animation="reveal-text-reading"
	const textsToReading = document.querySelectorAll(
		'[data-animation="reveal-text-reading"]',
	);
	textsToReading.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10) / 1000
			: 0;

		const split = SplitText.create(_this, {
			type: "words, chars",
			onSplit(self) {
				gsap.fromTo(
					self.chars,
					{
						opacity: 0.1,
					},
					{
						opacity: 1,
						delay: delay,
						stagger: 0.4,
						ease: "power1.out",
						scrollTrigger: {
							trigger: _this,
							start: "top 88%",
							end: "top 22%",
							scrub: true,
							// markers: true
						},
					},
				);
			},
		});
	});

	// data-animation="counter"
	const elementsToCounter = document.querySelectorAll(
		'[data-animation="counter"]',
	);
	elementsToCounter.forEach((_this) => {
		const delay = _this.getAttribute("data-animation-delay")
			? parseInt(_this.getAttribute("data-animation-delay"), 10)
			: 0;
		ScrollTrigger.create({
			trigger: _this,
			start: "top 100%",
			end: "bottom 0%",
			// markers: true,
			onEnter: () => {
				setTimeout(() => {
					gsap.from(_this, {
						duration: 1.5,
						textContent: 0,
						snap: { textContent: 1 },
						stagger: {
							each: 1.0,
							onUpdate: function () {
								this.targets()[0].innerHTML = Math.ceil(
									this.targets()[0].textContent,
								)
									// conserver les "."
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
									// conserver les espaces
									.toString()
									.replace(/\B(?=(\d{3})+(?!\d))/g, "&nbsp;");
							},
						},
					});
				}, delay);
			},
		});
	});

	// data-animation="skid"
	const elementsToskid = document.querySelectorAll([
		'body:not(.elementor-editor-active) [data-animation="skid"]',
	]);
	elementsToskid.forEach((_this) => {
		gsap.to(_this, {
			scrollTrigger: {
				trigger: _this,
				pin: true,
				pinSpacing: false,
				start: "top top",
				// markers: true,
			},
		});
	});

	/*
	// data-animation="draw"
	const elementsToDraw = document.querySelectorAll(['[data-animation="draw"]']);
	elementsToDraw.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 0;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			ease: "power1.inOut",
			duration: 1.2,
			delay: delay,
			scrollTrigger: {
				trigger: _this,
				start: "top 88%",
				// markers: true,
			},
		});
	});
	*/

	/*
	// data-animation="drawing"
	const elementsToDrawing = document.querySelectorAll([
		'[data-animation="drawing"]',
	]);
	elementsToDrawing.forEach((_this) => {
		const delay = _this.getAttribute("data-delay")
			? parseInt(_this.getAttribute("data-delay"), 10) / 100
			: 0;
		gsap.from(_this.querySelectorAll("g > *"), {
			drawSVG: 0,
			ease: "power1.inOut",
			duration: 1.8,
			delay: delay,
			stagger: 0.1,
			scrollTrigger: {
				trigger: _this,
				start: "top 88%",
				end: "bottom 44%",
				scrub: 1.1,
				// markers: true,
			},
		});
	});
	*/
};

// Actions exécutées après le preLoader d'accueil
const afterPreloader = () => {
	scrollEnabled(true);
	swiperInit();
	gsapDataAnimation();
	scrollToAnchor();
};

// GSAP : Gestion du preloader d'accueil
const gsapPreloader = () => {
	// Chargement des fonctions GSAP préalables
	initScrollSmooth();
	scrollEnabled(false);

	// Lancement de l'animation de chargement
	const loaderTimeline = gsap.timeline();
	loaderTimeline
		// animation
		.to("#page-preloader .preloader__wrapper", {
			delay: 0.3,
			duration: 0.6,
			ease: "power1.out",
			opacity: 0,
		})
		.set("#page-preloader", {
			visibility: "hidden",
		});

	// Functions succédentes
	loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.3);
};
